import { useEffect, useRef, useState } from "react";
import { apiFetch } from "@shared/lib/api";

// Shape of /api/v1/search/intent — mirrors IntentService#classify return value.
// The backend always returns 200 with `intent: null` for disabled / no_suggestion
// / upstream errors, so the hook never has to error-handle non-2xx paths.
export interface IntentTargetRecord {
  record_type: string;
  id: number;
  display_name: string;
}

export interface IntentActionPayload {
  action_id: string;
  label: string;
  subtitle: string;
  path: string;
  action?: string | null;
  prefill: Record<string, unknown>;
  prefill_kind?: string | null;
  target_record?: IntentTargetRecord | null;
  source_query: string;
}

export interface IntentQuestionPayload {
  question_type: string;
  summary: string;
  data: Record<string, unknown>;
  navigate_to: string | null;
  source_query: string;
}

// Discriminated union exposed to the UI. The `kind` field tags which variant
// is in hand so `SearchSuggestion` can render the right layout and click
// handler without prop-drilling the raw intent string everywhere.
export type IntentSuggestion =
  | ({ kind: "action" } & IntentActionPayload)
  | ({ kind: "question" } & IntentQuestionPayload);

export interface IntentResponse {
  intent: "action" | "question" | null;
  reason?: string;
  payload?: IntentActionPayload | IntentQuestionPayload;
}

const DEBOUNCE_MS = 500;

// Pulls a clean list of display names out of a question payload's `data` blob
// so the UI can render an item-level detail line under the summary. Shape of
// `data` varies by `question_type`, but answers that return record lists
// consistently include a `names: string[]` key. Returns null when the field is
// missing, empty, malformed, or unreasonably long — the caller treats null as
// "no detail line to render".
export function extractDetailNames(data: Record<string, unknown>): string[] | null {
  const names = data?.names;
  if (!Array.isArray(names)) return null;
  if (names.length === 0 || names.length > 20) return null;
  if (!names.every((n) => typeof n === "string")) return null;
  return names as string[];
}

// Returns the most recent intent suggestion for `query`, or null when the
// query is empty, intent is disabled server-side, or the LLM declined. Fires
// at most one in-flight call at a time; new queries abort the previous one.
//
// Intentionally separate from useGlobalSearch's record-search call so the
// instant heuristic + record results aren't blocked by the LLM hop.
export function useSearchIntent(query: string): { suggestion: IntentSuggestion | null } {
  const [suggestion, setSuggestion] = useState<IntentSuggestion | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setSuggestion(null);
      return;
    }

    timerRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;

      apiFetch<IntentResponse>(
        `/api/v1/search/intent?q=${encodeURIComponent(trimmed)}`,
        { signal: controller.signal }
      )
        .then(({ data }) => {
          if (controller.signal.aborted) return;
          if (data.intent === "action" && data.payload) {
            setSuggestion({ kind: "action", ...(data.payload as IntentActionPayload) });
          } else if (data.intent === "question" && data.payload) {
            setSuggestion({ kind: "question", ...(data.payload as IntentQuestionPayload) });
          } else {
            setSuggestion(null);
          }
        })
        .catch(() => {
          // Aborted or network error — leave previous suggestion in place
          // briefly to avoid flicker; the next keystroke will replace it.
        });
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query]);

  return { suggestion };
}
